import { useEffect, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { Mail, Unlock, AlertTriangle, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useAnalytics } from "@/hooks/useAnalytics";
import { useLanguage } from "@/contexts/LanguageContext";
import beamyCelebrate from "@/assets/beamy-celebrate.png";

const WaitlistForm = () => {
  const [email, setEmail] = useState("");
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const { trackEvent } = useAnalytics();
  const { t, language } = useLanguage();
  const successRef = useRef<HTMLDivElement>(null);
  const errorRef = useRef<HTMLDivElement>(null);

  const submitWaitlist = async () => {
    setIsLoading(true);
    setSubmitError(null);
    try {
      const { error } = await supabase.functions.invoke('send-waitlist-email', { body: { email, language } });
      if (error) throw error;
      setIsSubmitted(true);
      toast({ title: t("waitlist.successToast"), description: t("waitlist.successToastDesc") });
      trackEvent("waitlist_form_submitted", { form_type: "waitlist" });
    } catch (error) {
      console.error('Error saving to database:', error);
      setSubmitError(t("waitlist.error.saveDesc"));
      toast({ title: t("waitlist.error.save"), description: t("waitlist.error.saveDesc"), variant: "destructive" });
      trackEvent("waitlist_form_error", { form_type: "waitlist" });
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !email.includes('@')) {
      toast({ title: t("waitlist.error.invalid"), description: t("waitlist.error.invalidDesc"), variant: "destructive" });
      return;
    }
    await submitWaitlist();
  };

  useEffect(() => {
    if (submitError && errorRef.current) {
      errorRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      requestAnimationFrame(() => errorRef.current?.focus({ preventScroll: true }));
    }
  }, [submitError]);

  useEffect(() => {
    if (isSubmitted && successRef.current) {
      successRef.current.scrollIntoView({ behavior: "smooth", block: "center" });
      requestAnimationFrame(() => successRef.current?.focus({ preventScroll: true }));
    }
  }, [isSubmitted]);

  if (isSubmitted) {
    return (
      <section id="waitlist" className="py-24 bg-ink-800" aria-labelledby="waitlist-success-title">
        <div className="container mx-auto px-6 text-center">
          <div ref={successRef} role="status" aria-live="polite" tabIndex={-1}
            className="max-w-2xl mx-auto bg-ink-700 border border-white/[0.06] rounded-3xl p-10 sm:p-12 text-left outline-none focus-visible:ring-2 focus-visible:ring-violet">
            <div className="flex flex-col items-center text-center">
              <div className="relative flex justify-center items-end mb-2 h-36 sm:h-40 w-full">
                <div aria-hidden className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-[55%] w-56 h-56 sm:w-64 sm:h-64 rounded-full pointer-events-none"
                  style={{ background: "radial-gradient(circle, rgba(181,160,255,0.32) 0%, rgba(139,111,255,0.14) 38%, transparent 75%)", filter: "blur(8px)" }} />
                <img src={beamyCelebrate} alt="" aria-hidden draggable={false}
                  className="relative w-32 sm:w-36 object-contain select-none pointer-events-none animate-float"
                  style={{ transformOrigin: "center bottom" }} />
              </div>
              <h2 id="waitlist-success-title" className="font-display text-3xl sm:text-4xl text-white mb-3">{t("waitlist.success.title")}</h2>
              <p className="text-base sm:text-lg text-white/65 mb-8">{t("waitlist.success.desc")}</p>
            </div>

            <div className="text-[11px] uppercase tracking-[0.12em] text-violet font-semibold mb-4">{t("waitlist.success.nextTitle")}</div>

            <ol className="flex flex-col gap-3 mb-8">
              <li className="flex gap-4 items-start bg-ink-800/60 border border-white/[0.06] rounded-2xl p-4">
                <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-violet/15 flex items-center justify-center">
                  <Mail className="h-4 w-4 text-violet" aria-hidden />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white mb-1">
                    <span className="text-violet/70 mr-1.5">01</span>{t("waitlist.success.next1Title")}
                  </div>
                  <div className="text-[13px] text-white/55 leading-relaxed">{t("waitlist.success.next1Desc")}</div>
                </div>
              </li>
              <li className="flex gap-4 items-start bg-ink-800/60 border border-white/[0.06] rounded-2xl p-4">
                <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-violet/15 flex items-center justify-center">
                  <Unlock className="h-4 w-4 text-violet" aria-hidden />
                </div>
                <div>
                  <div className="text-sm font-semibold text-white mb-1">
                    <span className="text-violet/70 mr-1.5">02</span>{t("waitlist.success.next2Title")}
                  </div>
                  <div className="text-[13px] text-white/55 leading-relaxed">{t("waitlist.success.next2Desc")}</div>
                </div>
              </li>
            </ol>

            <p className="text-xs text-white/45 text-center">{t("waitlist.success.social")}</p>
          </div>
        </div>
      </section>
    );
  }

  return (
    <section id="waitlist" className="py-24 bg-ink-800 relative overflow-hidden">
      <div className="absolute -top-32 left-1/2 -translate-x-1/2 w-[600px] h-[600px] bg-violet/15 rounded-full blur-[120px] pointer-events-none" />
      <div className="container mx-auto px-6 text-center relative z-10">
        <div className="max-w-2xl mx-auto">
          <div className="eyebrow mb-4">{t("eyebrow.earlyAccess")}</div>
          <h2 className="font-display text-4xl md:text-5xl text-white mb-4">{t("waitlist.title")}</h2>
          <p className="text-lg text-white/65 mb-10">{t("waitlist.desc")}</p>
          <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-3 max-w-xl mx-auto" noValidate>
            <Input type="email" placeholder={t("waitlist.placeholder")} value={email}
              onChange={e => { setEmail(e.target.value); if (submitError) setSubmitError(null); }}
              aria-invalid={submitError ? true : undefined}
              className="flex-1 bg-ink-700 border-white/10 text-white placeholder:text-white/40 rounded-full px-6 py-6 text-base focus-visible:ring-violet"
              required />
            <Button type="submit" disabled={isLoading} className="btn-violet whitespace-nowrap">
              {isLoading ? t("waitlist.submitting") : t("waitlist.submit")}
            </Button>
          </form>

          {submitError && (
            <div ref={errorRef} role="alert" tabIndex={-1}
              className="max-w-xl mx-auto mt-5 bg-destructive/10 border border-destructive/40 rounded-2xl p-4 sm:p-5 flex gap-3 sm:gap-4 items-start text-left outline-none">
              <div className="flex-shrink-0 w-9 h-9 rounded-xl bg-destructive/20 flex items-center justify-center">
                <AlertTriangle className="h-4 w-4 text-destructive" aria-hidden />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-semibold text-white mb-1">{t("waitlist.error.save")}</div>
                <p className="text-[13px] text-white/65 leading-relaxed mb-3">{submitError}</p>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" onClick={submitWaitlist} disabled={isLoading}
                    className="h-9 px-4 rounded-full bg-destructive text-destructive-foreground hover:bg-destructive/90 text-xs font-semibold uppercase tracking-wide">
                    {isLoading ? t("waitlist.submitting") : t("waitlist.error.retry")}
                  </Button>
                  <Button type="button" onClick={() => setSubmitError(null)} variant="ghost"
                    className="h-9 px-3 rounded-full text-white/60 hover:text-white hover:bg-white/5 text-xs font-semibold uppercase tracking-wide">
                    <X className="h-3.5 w-3.5 mr-1" aria-hidden />
                    {t("waitlist.error.dismiss")}
                  </Button>
                </div>
              </div>
            </div>
          )}

          <p className="text-xs text-white/40 mt-4">{t("waitlist.privacy")}</p>
        </div>
      </div>
    </section>
  );
};

export default WaitlistForm;
